import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { contains, defaultTo, isEmpty, path, find, propEq, sortBy, prop } from 'ramda';
import moment from 'moment';

import magic from '../../../../util/magic';
import { createMarkup } from '../../../../util/helpers';
import { apiDeleteRegistration, apiGetRegistration } from '../../../../api/registration';
import registrationActions from '../../../../redux/registration';
import { routes } from '../../../../routes';
import { handleErrorBase, handleSuccessBase } from '../../../../util/forms';

import Card from '../../../../components/Card';
import FlaggedUserModal from '../../../../components/Modal/FlaggedUserModal';
import EditRegistrationModal from '../../../../components/Modal/EditRegistrationModal';
import RejectRegistrationModal from '../../../../components/Modal/RejectRegistrationModal';
import ApproveRegistrationModal from '../../../../components/Modal/ApproveRegistrationModal';
import ModuleSectionModal from '../../../../components/Modal/ModuleSectionModal';
import ExamsTable from '../../CertificationsAndTraining/Registration/Steps/Components/ExamsTable';
import SummaryTable from '../../CertificationsAndTraining/Registration/Steps/Components/SummaryTable';
import FinalSummaryTable from '../../CertificationsAndTraining/Registration/Steps/Components/FinalSummaryTable';
import { isNilOrEmpty } from '../../../../util/redux';

const mapStore = (store) => ({
  registration: store.registration,
});

const mapDispatch = {
  ...registrationActions,
};

const propTypes = {
  match: PropTypes.object,
  registration: PropTypes.object,
  overwriteRegistration: PropTypes.func,
};

class RegistrationReview extends Component {
  state = {
    loaded: false,
    showApproveModal: false,
    approvalTrainingId: null,
    showRejectModal: false,
    showModuleSectionModal: false,
    showEditModal: false,
    moduleForSections: {},
  };

  componentDidMount() {
    const { match, registration, overwriteRegistration } = this.props;
    const selectedRegistrationId = match.params.id;

    if (path(['id'], registration) == selectedRegistrationId) {
      this.setState({ loaded: true });
    } else {
      apiGetRegistration(selectedRegistrationId)
        .then((resp) => overwriteRegistration(resp.data))
        .then(() => this.setState({ loaded: true }));
    }
  }

  renderTrainingCanvasStatus = (training, badge, isLastTraining) => {
    const sectionId = path(['extra', 'canvasSection'], training);
    if (badge && !isLastTraining) return <span>Approved</span>;

    if (!isNilOrEmpty(sectionId) && !isLastTraining) {
      return (
        <button
          className="btn btn-primary"
          onClick={() => this.setState({ showApproveModal: true, approvalTrainingId: training.categoryId })}
        >
          Approve
        </button>
      );
    }

    if (isNilOrEmpty(sectionId)) {
      return (
        <button
          className="btn btn-primary"
          onClick={() => this.setState({ showModuleSectionModal: true, moduleForSections: training })}
        >
          Get Sections
        </button>
      );
    }
  };

  renderModuleApprovals = () => {
    const { registration } = this.props;
    const user = registration.user;
    const badges = defaultTo([], user.userTrainings);

    // TODO: Fix this hardcoded number with something like checking if there's a canvas ID
    const trainings = sortBy(prop('id'), registration.trainings);

    return (
      <Card className="mt-4">
        <Card.Header>{registration.trainingId ? 'Training' : 'Modules'}</Card.Header>
        <Card.Body>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Canvas</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {trainings.map((training, i) => {
                const badge = find(propEq('trainingId', training.categoryId), badges);
                // TODO: Too hardcoded
                const lastTraining = i == 2;

                return (
                  <tr key={training.id}>
                    <td>{training.name}</td>
                    <td>
                      {user.canvasId && (
                        <a href={`https://financialfitnesscoach.instructure.com/accounts/1/users/${user.canvasId}`}>View Progress</a>
                      )}
                    </td>
                    <td>{this.renderTrainingCanvasStatus(training, badge, lastTraining)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card.Body>
      </Card>
    );
  };

  renderRegistrationReviews = (reviews) => (
    <Fragment>
      <h4 className="bold-blue-heading mt-5 mb-3">Registration Reviews</h4>
      <table className="table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Reviewer</th>
            <th>Message</th>
            <th>Step</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {reviews.map((review) => (
            <tr key={review.id}>
              <td>{moment(review.createdAt).format('MM/DD/YYYY')}</td>
              <td>
                {path(['user', 'firstName'], review)} {path(['user', 'lastName'], review)}
              </td>
              <td dangerouslySetInnerHTML={createMarkup(review.message)} />
              <td>{path(['extra', 'newStep'], review)}</td>
              <td>
                {review.approved ? (
                  <span className="badge badge-success">Approved</span>
                ) : (
                  <span className="badge badge-warning">Rejected</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Fragment>
  );

  renderApprovalButtons = () => (
    <div className="text-md-right">
      <button type="button" className="btn btn-danger mr-2" onClick={() => this.setState({ showRejectModal: true })}>
        Reject
      </button>
      <button type="button" className="btn btn-primary" onClick={() => this.setState({ showApproveModal: true })}>
        Approve
      </button>
    </div>
  );

  render() {
    if (!this.state.loaded) return <noscript />;
    const {
      approvalTrainingId,
      showApproveModal,
      showRejectModal,
      showModuleSectionModal,
      moduleForSections,
      showEditModal,
    } = this.state;
    const { registration, history, updateRegistration } = this.props;
    const reviews = defaultTo([], registration.reviews);
    const preRegistrationReviews = reviews.filter((review) =>
      contains(path(['extra', 'previousStep'], review), magic.preRegistrationSteps),
    );
    const postRegistrationReviews = reviews.filter((review) =>
      contains(path(['extra', 'previousStep'], review), magic.postRegistrationSteps),
    );
    const isWaitingForApproval = registration.status == magic.registrationApproval;
    const isWaitingForPostApproval =
      registration.status == magic.finalApproval || registration.status == magic.experienceApproval;
    const isModuleSteps = defaultTo('', registration.step).indexOf(magic.moduleStepPrefix) != -1;
    const isPostRegistration = contains(registration.status, magic.postRegistrationStatuses);
    const extension = registration.extension;

    return (
      <div>
        <FlaggedUserModal
          user={registration.user}
        >
        </FlaggedUserModal>
        <Card>
          <Card.Header>
            <div className="float-left">
              <Link to={routes.admin.users.profileBuilder(path(['user', 'id'], registration))} className="d-block">
                {path(['user', 'firstName'], registration)} {path(['user', 'lastName'], registration)}
                {registration.militarySpouse ? ' (Military Spouse)' : ''}
              </Link>
              {registration.deadline && (
                <span className="font-reset d-block">
                  Deadline: {moment(registration.deadline).format('MM/DD/YYYY')}
                </span>
              )}
              {registration.enrolledOn && (
                <span className="font-reset d-block">
                  Enrolled On: {moment(registration.enrolledOn).format('MM/DD/YYYY')}
                </span>
              )}
              {registration.requiredHours > 0 && (
                <span className="font-reset d-block">Required Experience Hours: {registration.requiredHours}</span>
              )}
              {extension && extension.approved != null && (
                <span className="font-reset d-block">
                  Extension:
                  <Link to={routes.admin.extensions.reviewBuilder(extension.id)}>
                    {extension.approved ? ' Approved' : ' Rejected'}
                  </Link>
                </span>
              )}
            </div>
            <button
              className="btn btn-danger float-right"
              onClick={() =>
                apiDeleteRegistration(registration.id)
                  .then(() => handleSuccessBase())
                  .then(() => history.push(routes.admin.courseRegistrations.root))
                  .catch((err) => {
                    console.error(err);
                    handleErrorBase('Could not delete registration');
                  })
              }
            >
              Delete
            </button>
            <button className="btn btn-primary float-right mr-3" onClick={() => this.setState({ showEditModal: true })}>
              Edit
            </button>
          </Card.Header>
          <Card.Body>
            <SummaryTable registration={registration} admin={true} {...this.props} />
            {!isEmpty(preRegistrationReviews) && this.renderRegistrationReviews(preRegistrationReviews)}
            {isWaitingForApproval && this.renderApprovalButtons()}
          </Card.Body>
        </Card>
        {isPostRegistration && !isNilOrEmpty(registration.trainings) && this.renderModuleApprovals()}
        {isPostRegistration && !registration.trainingId && (
          <Card className="mt-4">
            <Card.Header>Post Registration</Card.Header>
            <Card.Body>
              <ExamsTable admin registration={registration} />
              <FinalSummaryTable admin user={registration.user} registration={registration} />
              {!isEmpty(postRegistrationReviews) && this.renderRegistrationReviews(postRegistrationReviews)}
              {isWaitingForPostApproval && this.renderApprovalButtons()}
            </Card.Body>
          </Card>
        )}
        <ApproveRegistrationModal
          show={showApproveModal}
          onHide={() => this.setState({ showApproveModal: false })}
          registration={registration}
          trainingId={approvalTrainingId}
        />
        <RejectRegistrationModal
          show={showRejectModal}
          onHide={() => this.setState({ showRejectModal: false })}
          registration={registration}
        />
        <ModuleSectionModal
          show={showModuleSectionModal}
          onHide={() => this.setState({ showModuleSectionModal: false })}
          registration={registration}
          training={moduleForSections}
        />
        <EditRegistrationModal
          show={showEditModal}
          onHide={() => this.setState({ showEditModal: false })}
          registration={registration}
          updateRegistration={updateRegistration}
        />
      </div>
    );
  }
}

RegistrationReview.propTypes = propTypes;

export default withRouter(connect(mapStore, mapDispatch)(RegistrationReview));
